import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../helpers/AuthContext';
import AffiliateReferralPanel from '../Affiliates/AffiliateReferralPanel';
import './PlanBillingPage.css';

const PAGE_SIZE = 25;

const transactionLabels = {
  plan_subscription_deposit: 'PayPal plan payment',
  plan_renewal: 'Plan term applied',
  plan_subscription_refund: 'PayPal refund',
  plan_subscription_reversal: 'PayPal reversal',
};

const modeLabels = {
  off: 'Legacy billing',
  shadow: 'Billing observation',
  authoritative: 'Ledger billing',
};

const formatCents = (amountCents, currency = 'USD') => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: currency || 'USD',
}).format((amountCents || 0) / 100);

const formatDate = value => {
  if (!value) return 'Not available';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(value));
};

// "Suspended" is reserved for the admin enforcement action; PayPal's SUSPENDED reads "Paused".
const STATUS_LABELS = { SUSPENDED: 'Paused' };

function StatusBadge({ value }) {
  const normalized = String(value || 'unknown').toLowerCase().replace(/[_\s]+/g, '-');
  return <span className={`plan-billing-status ${normalized}`}>{STATUS_LABELS[value] || value || 'Unknown'}</span>;
}

function WarningList({ warnings }) {
  if (!warnings?.length) return null;
  return (
    <div className="plan-billing-warnings" aria-label="Billing notices">
      {warnings.map(warning => (
        <div className={`flash ${warning.code === 'provider-data-stale' ? 'warning' : 'error'}`}
          key={warning.code} role={warning.code === 'held-payment' ? 'alert' : 'status'}>
          <p>{warning.message}</p>
        </div>
      ))}
    </div>
  );
}

const unavailabilityCopy = {
  'realm-ineligible': 'Not offered for this storefront.',
  'active-subscription': 'Already included in your active plan.',
  'outside-renewal-window': 'Opens closer to your renewal date.',
};

function ComparisonCell({ cell: cellValue }) {
  if (!cellValue) return <span className="plan-billing-cell-empty" aria-hidden="true">—</span>;
  if (cellValue.state === 'included') {
    return <i className="fa-solid fa-check plan-billing-cell-included" aria-label="Included" title="Included" />;
  }
  if (cellValue.state === 'not-included') {
    return <i className="fa-solid fa-xmark plan-billing-cell-not-included" aria-label="Not included" title="Not included" />;
  }
  return <span>{cellValue.text}</span>;
}

function TierPriceOption({ option, canPurchase, purchaseUrl }) {
  return (
    <div className="plan-billing-tier-price">
      <strong>{formatCents(option.priceCents, option.currency)}</strong>
      <small>per {option.cadence === 'annual' ? 'year' : 'month'}</small>
      {option.available ? (
        <a className={`button ${canPurchase ? '' : 'secondary'}`} href={purchaseUrl}>
          {canPurchase ? 'Choose this plan' : 'View plan details'}
        </a>
      ) : (
        <span className="plan-billing-unavailable">
          {unavailabilityCopy[option.unavailabilityReason] || 'Not available right now.'}
        </span>
      )}
    </div>
  );
}

function PlanComparison({ catalog, canPurchase, purchaseUrl }) {
  const { tiers, comparisonRows } = catalog;
  return (
    <section className="plan-billing-section" aria-labelledby="plan-comparison-title">
      <div className="plan-billing-section-heading">
        <div>
          <p className="plan-billing-eyebrow">Plan comparison</p>
          <h2 id="plan-comparison-title">Current plan options</h2>
        </div>
        <a href="/help/compare-plans" target="_blank" rel="noopener">
          Compare all features <i className="fa-solid fa-arrow-up-right-from-square" />
        </a>
      </div>
      <div className="plan-billing-comparison-scroll">
        <table className="plan-billing-comparison-table">
          <thead>
            <tr>
              <th scope="col" className="plan-billing-comparison-feature-header">
                <span className="sr-only">Feature</span>
              </th>
              {tiers.map(tier => (
                <th scope="col" key={tier.id}>
                  <h3>{tier.name}</h3>
                  {tier.purchaseOptions.length
                    ? tier.purchaseOptions.map(option => (
                      <TierPriceOption key={option.type} option={option} canPurchase={canPurchase}
                        purchaseUrl={purchaseUrl} />
                    ))
                    : <p className="plan-billing-tier-included">Included automatically</p>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {comparisonRows.map(row => (
              <tr key={row.key}>
                <th scope="row">{row.label}</th>
                {tiers.map(tier => (
                  <td key={tier.id}><ComparisonCell cell={row.cells[tier.id]} /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function TransactionHistory({ transactions, meta, legacy, offset, setOffset }) {
  const canGoBack = offset > 0;
  const canGoForward = offset + meta.limit < meta.total;
  return (
    <section className="plan-billing-section" aria-labelledby="billing-history-title">
      <div className="plan-billing-section-heading">
        <div>
          <p className="plan-billing-eyebrow">Local ledger history</p>
          <h2 id="billing-history-title">Plan transactions</h2>
        </div>
        <a href={legacy.billingHistoryUrl}>View older PayPal history</a>
      </div>
      {!transactions.length ? (
        <div className="plan-billing-empty">
          <i className="fa-solid fa-receipt" aria-hidden="true" />
          <h3>No plan payments yet</h3>
          <p>Your PayPal plan payments will show up here once they're processed.</p>
        </div>
      ) : (
        <div className="plan-billing-transactions">
          {transactions.map(transaction => (
            <article className="plan-billing-transaction" key={transaction.uuid}>
              <div>
                <h3>{transactionLabels[transaction.type] || transaction.type}</h3>
                <p>{formatDate(transaction.occurredAt)}</p>
              </div>
              <div className="plan-billing-transaction-amount">
                <strong>
                  {transaction.direction === 'out' ? '−' : '+'}
                  {formatCents(transaction.amountCents, transaction.currency)}
                </strong>
                <StatusBadge value={transaction.isFlagged ? 'Needs review' : transaction.status} />
              </div>
              {transaction.providerTransactionId && (
                <code title="PayPal transaction ID">
                  {transaction.providerTransactionId}
                </code>
              )}
            </article>
          ))}
        </div>
      )}
      {(canGoBack || canGoForward) && (
        <nav className="plan-billing-pagination" aria-label="Billing history pages">
          <button className="button secondary" type="button" disabled={!canGoBack}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
            Previous
          </button>
          <span>Showing {offset + 1}–{Math.min(offset + meta.limit, meta.total)} of {meta.total}</span>
          <button className="button secondary" type="button" disabled={!canGoForward}
            onClick={() => setOffset(offset + PAGE_SIZE)}>
            Next
          </button>
        </nav>
      )}
    </section>
  );
}

export default function PlanBillingPage() {
  const auth = useAuth();
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [offset, setOffset] = useState(0);

  const loadBilling = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await auth.fetch(
        `${auth.API_URL}/v1/seller/payments/plan-billing?limit=${PAGE_SIZE}&offset=${offset}`,
      );
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Unable to load billing details.');
      setBilling(body);
    } catch (loadError) {
      setError(loadError.message || 'Unable to load billing details.');
    } finally {
      setLoading(false);
    }
  }, [auth.API_URL, auth.fetch, offset]);

  useEffect(() => {
    loadBilling();
  }, [loadBilling]);

  if (loading && !billing) {
    return (
      <div className="plan-billing-loading" role="status">
        <i className="fa-solid fa-circle-notch fa-spin" aria-hidden="true" />
        <p>Loading your billing details…</p>
      </div>
    );
  }

  if (error && !billing) {
    return (
      <section className="plan-billing-error" role="alert">
        <h2>Billing details are unavailable</h2>
        <p>{error}</p>
        <button className="button" type="button" onClick={loadBilling}>Try again</button>
        <a href="/account/plan">Open legacy plan management</a>
      </section>
    );
  }

  const { data, meta } = billing;
  const { plan, subscription, ledger, actions, warnings, legacy, catalog } = data;
  const isExpired = ['expired', 'trial-expired'].includes(plan.status);
  const isCanceledButActive = plan.status === 'active' && subscription?.status === 'CANCELLED';
  const isSuspended = subscription?.status === 'SUSPENDED';

  return (
    <div className="plan-billing-page" aria-busy={loading}>
      {error && <div className="flash error" role="alert"><p>{error}</p></div>}
      <WarningList warnings={warnings} />
      {isExpired && (
        <div className="flash error" role="alert">
          <p>Your plan access has expired. Choose a plan to restore Marketplace visibility.</p>
        </div>
      )}
      {isCanceledButActive && (
        <div className="flash warning" role="status">
          <p>PayPal auto-renew is canceled. Your plan access remains active through{' '}
            <strong>{formatDate(plan.paidThrough)}</strong>.</p>
        </div>
      )}
      {isSuspended && (
        <div className="flash warning" role="status">
          <p>PayPal auto-renew is paused. Your plan access remains active through{' '}
            <strong>{formatDate(plan.paidThrough)}</strong> — resume it from the legacy plan page
            to keep billing automatically.</p>
        </div>
      )}

      <div className="plan-billing-summary-grid">
        <section className="plan-billing-summary-card" aria-labelledby="plan-access-title">
          <p className="plan-billing-eyebrow">Plan access</p>
          <div className="plan-billing-card-title">
            <h2 id="plan-access-title">{plan.name || 'No paid plan'}</h2>
            <StatusBadge value={plan.status} />
          </div>
          <dl>
            <div><dt>Paid through</dt><dd>{formatDate(plan.paidThrough)}</dd></div>
            <div><dt>Current price</dt><dd>{plan.price
              ? formatCents(plan.price.amountCents, plan.price.currency)
              : 'Free'}</dd></div>
            <div><dt>Renewal available</dt><dd>{plan.canBuyPlan
              ? 'Now'
              : formatDate(plan.renewalAvailableAt)}</dd></div>
          </dl>
        </section>

        <section className="plan-billing-summary-card" aria-labelledby="auto-renew-title">
          <p className="plan-billing-eyebrow">PayPal auto-renew</p>
          <div className="plan-billing-card-title">
            <h2 id="auto-renew-title">{subscription ? 'Subscription' : 'Not connected'}</h2>
            <StatusBadge value={subscription?.status || 'None'} />
          </div>
          <dl>
            <div><dt>Next expected charge</dt><dd>{formatDate(subscription?.nextBillingAt)}</dd></div>
            <div><dt>Provider refreshed</dt><dd>{formatDate(subscription?.lastSyncedAt)}</dd></div>
            <div><dt>Billing processing</dt><dd>{modeLabels[ledger.mode] || ledger.mode}</dd></div>
          </dl>
          <p className="plan-billing-action-row">
            {actions.canCancel ? (
              <a className="button secondary" href="/account/plan/cancel">Cancel auto-renew</a>
            ) : (
              <a className="button secondary" href={legacy.planUrl}>Manage plan</a>
            )}
          </p>
        </section>
      </div>

      <PlanComparison catalog={catalog} canPurchase={actions.canPurchase}
        purchaseUrl={legacy.planUrl} />

      <AffiliateReferralPanel />

      {!meta.historyCompleteFrom && (
        <p className="plan-billing-history-note">
          Local history is still being established. Use the legacy PayPal history link for older
          transactions.
        </p>
      )}

      <TransactionHistory transactions={data.transactions} meta={meta} legacy={legacy}
        offset={offset} setOffset={setOffset} />
    </div>
  );
}
